(() => {
'use strict';
if (window.OrlavenCart) return;
const cartDrawer = document.querySelector('[data-or-cart-drawer]');
if (!cartDrawer) return;
const cartDrawerMessage = cartDrawer.querySelector('[data-or-cart-drawer-message]');
const cartDrawerItems = cartDrawer.querySelector('[data-or-cart-drawer-items]');
const cartDrawerSubtotal = cartDrawer.querySelector('[data-or-cart-drawer-subtotal]');
const routesRoot = window.Shopify?.routes?.root || '/';
const escapeHtml = (value = '') => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const formatCartMoney = (cents = 0, currency = 'USD') => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: Number(cents) % 100 === 0 ? 0 : 2
    }).format(Number(cents || 0) / 100);
  } catch (error) {
    return `$${(Number(cents || 0) / 100).toFixed(Number(cents) % 100 === 0 ? 0 : 2)}`;
  }
};

let returnFocus;
let closeTimer;
const openCartDrawer = () => {
  clearTimeout(closeTimer);
  if (!cartDrawer.open) {
    returnFocus = document.activeElement;
    cartDrawer.showModal();
    cartDrawer.querySelector('.orpdp__cart-close')?.focus();
  }
  document.documentElement.classList.add('orpdp-cart-open');
  requestAnimationFrame(() => cartDrawer.classList.add('is-open'));
};
const closeCartDrawer = () => {
  cartDrawer.classList.remove('is-open');
  closeTimer = setTimeout(() => {
    cartDrawer.close();
    document.documentElement.classList.remove('orpdp-cart-open');
    if (returnFocus?.isConnected) returnFocus.focus();
  }, matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 260);
};
const cartUrl = (value, image = false) => {
  try {
    const url = new URL(value, location.origin);
    if (!['https:', 'http:'].includes(url.protocol)) return '#';
    if (image) url.searchParams.set('width', '220');
    return url.href;
  } catch { return '#'; }
};

const renderCartDrawer = (cart, message = '') => {
  if (!cartDrawer || !cartDrawerItems) return;
  const currency = cart?.currency || window.Shopify?.currency?.active || 'USD';
  const items = Array.isArray(cart?.items) ? cart.items : [];
  if (cartDrawerMessage) {
    cartDrawerMessage.textContent = message || (items.length ? 'Your bag is ready.' : 'Your bag is empty.');
    cartDrawerMessage.dataset.tone = message ? 'success' : 'neutral';
    cartDrawerMessage.hidden = !message && items.length > 0;
  }
  cartDrawerItems.innerHTML = items.length ? items.map((item) => {
    const quantity = Math.max(1, Number(item.quantity || 1) || 1);
    const linePrice = Number(item.final_line_price ?? item.line_price ?? 0) || 0;
    const unitPrice = Number(item.final_price ?? item.price ?? (linePrice ? Math.round(linePrice / quantity) : 0)) || 0;
    const options = (item.variant_options || [])
      .filter((option) => option && option !== 'Default Title')
      .join(' / ');
    const properties = Object.entries(item.properties || {})
      .filter(([key, value]) => !key.startsWith('_') && value != null && String(value).trim() !== '')
      .map(([key, value]) => `<p class="orpdp__cart-item-options">${escapeHtml(key)}: ${escapeHtml(value)}</p>`)
      .join('');
    const image = item.image
      ? `<img src="${escapeHtml(cartUrl(item.image, true))}" alt="${escapeHtml(item.product_title || item.title || 'Cart item')}" loading="lazy">`
      : '<span class="orpdp__cart-item-placeholder" aria-hidden="true"></span>';
    return `
      <article class="orpdp__cart-item" data-or-cart-line-key="${escapeHtml(item.key)}">
        <a class="orpdp__cart-item-image" href="${escapeHtml(cartUrl(item.url || routesRoot + 'cart'))}">${image}</a>
        <div class="orpdp__cart-item-copy">
          <a class="orpdp__cart-item-title" href="${escapeHtml(cartUrl(item.url || routesRoot + 'cart'))}">${escapeHtml(item.product_title || item.title || 'Item')}</a>
          ${options ? `<p class="orpdp__cart-item-options">${escapeHtml(options)}</p>` : ''}
          ${properties}
          <div class="orpdp__cart-item-controls" aria-label="Quantity selector">
            <button type="button" data-or-cart-quantity data-or-cart-key="${escapeHtml(item.key)}" data-or-cart-quantity-delta="-1" aria-label="Decrease quantity">−</button>
            <span data-or-cart-current-quantity>${quantity}</span>
            <button type="button" data-or-cart-quantity data-or-cart-key="${escapeHtml(item.key)}" data-or-cart-quantity-delta="1" aria-label="Increase quantity">+</button>
          </div>
        </div>
        <div class="orpdp__cart-item-side">
          <button type="button" class="orpdp__cart-remove" data-or-cart-quantity data-or-cart-key="${escapeHtml(item.key)}" data-or-cart-next-quantity="0">Remove</button>
          <div class="orpdp__cart-item-price-stack">
            <strong class="orpdp__cart-item-price">${formatCartMoney(unitPrice, currency)}</strong>
            ${quantity > 1 ? `
              <span class="orpdp__cart-line-total">
                ${formatCartMoney(linePrice, currency)} total
              </span>
            ` : ''}
            </div>
        </div>
      </article>
    `;
  }).join('') : '<div class="orpdp__cart-empty">Your bag is empty.</div>';
  const checkout = cartDrawer.querySelector('[data-or-cart-checkout]');
  checkout.setAttribute('aria-disabled', String(items.length === 0));
  checkout.tabIndex = items.length ? 0 : -1;
  if (items.length) checkout.href = routesRoot + 'checkout';
  else checkout.removeAttribute('href');
  if (cartDrawerSubtotal) {
    cartDrawerSubtotal.textContent = formatCartMoney(cart?.total_price || 0, currency);
  }
};

const renderCartDrawerPending = (message = 'Updating your bag…') => {
  if (!cartDrawer || !cartDrawerItems) return;
  if (cartDrawerMessage) {
    cartDrawerMessage.textContent = message;
    cartDrawerMessage.dataset.tone = 'success';
    cartDrawerMessage.hidden = false;
  }
  cartDrawerItems.innerHTML = `
    <div class="orpdp__cart-pending" role="status" aria-live="polite">
      <span aria-hidden="true"></span>
      <p>Updating your bag…</p>
    </div>
  `;
  openCartDrawer();
};

const showCartDrawerMessage = async ({ title = '', message = '', tone = 'success', cart = null } = {}) => {
  const currentCart = cart || await runCartMutation(() => getCart({ timeout: 4200 })).catch(() => null);
  const drawerMessage = tone === 'success' && message
    ? `Added to bag — ${message}`
    : title && message
      ? `${title} ${message}`
      : title || message;
  if (currentCart) {
    renderCartDrawer(currentCart, drawerMessage);
  } else if (cartDrawerMessage) {
    cartDrawerMessage.textContent = drawerMessage;
    cartDrawerMessage.dataset.tone = tone;
    cartDrawerMessage.hidden = false;
  }
  if (cartDrawerMessage) cartDrawerMessage.dataset.tone = tone;
  openCartDrawer();
};

const setCartLinePending = (key, quantity) => {
  const line = [...(cartDrawerItems?.querySelectorAll('[data-or-cart-line-key]') || [])]
    .find((candidate) => candidate.dataset.orCartLineKey === key);
  if (line) {
    line.setAttribute('aria-busy', 'true');
    if (quantity > 0) {
      const quantityLabel = line.querySelector('[data-or-cart-current-quantity]');
      if (quantityLabel) quantityLabel.textContent = String(quantity);
    }
    line.querySelectorAll('[data-or-cart-quantity]').forEach((button) => {
      button.disabled = quantity === 0;
    });
  }
  if (cartDrawerMessage) {
    cartDrawerMessage.textContent = quantity === 0 ? 'Removing item…' : `Updating quantity to ${quantity}…`;
    cartDrawerMessage.dataset.tone = 'neutral';
    cartDrawerMessage.hidden = false;
  }
  if (cartDrawerSubtotal) cartDrawerSubtotal.textContent = 'Updating…';
};

const restoreCartLineAfterFailure = (key, quantity) => {
  const line = [...(cartDrawerItems?.querySelectorAll('[data-or-cart-line-key]') || [])]
    .find((candidate) => candidate.dataset.orCartLineKey === key);
  if (!line) return;
  line.removeAttribute('aria-busy');
  const quantityLabel = line.querySelector('[data-or-cart-current-quantity]');
  if (quantityLabel) quantityLabel.textContent = String(quantity);
  line.querySelectorAll('[data-or-cart-quantity]').forEach((button) => {
    button.disabled = false;
  });
};

// CART_MUTATION_QUEUE_START
// ponytail: one queue per storefront tab; Shopify's cart is one shared resource.
let cartMutationTail = Promise.resolve();
const runCartMutation = (mutation) => {
  const result = cartMutationTail.then(mutation, mutation);
  cartMutationTail = result.catch(() => null);
  return result;
};
// CART_MUTATION_QUEUE_END

const changeCartLine = (key, quantity) => runCartMutation(async () => {
  const response = await fetch(`${routesRoot}cart/change.js`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ id: key, quantity: Math.max(0, Number(quantity) || 0) })
  });
  const cart = await response.json().catch(() => null);
  if (!response.ok) throw new Error(cart?.description || cart?.message || 'Cart update failed.');
  return cart;
});

// CART_LINE_QUEUE_START
const cartLineUpdates = new Map();
let cartUpdateWorker = null;

const refreshPendingCartLines = () => {
  cartLineUpdates.forEach((state, key) => setCartLinePending(key, state.desiredQuantity));
};

const flushCartLineUpdates = async () => {
  while (cartLineUpdates.size) {
    const [key, state] = cartLineUpdates.entries().next().value;
    const requestedQuantity = state.desiredQuantity;
    try {
      const cart = await changeCartLine(key, requestedQuantity);
      const confirmedLine = Array.isArray(cart?.items)
        ? cart.items.find((item) => item.key === key)
        : null;
      state.confirmedQuantity = Math.max(0, Number(confirmedLine?.quantity) || 0);
      if (state.desiredQuantity !== requestedQuantity) continue;
      cartLineUpdates.delete(key);
      syncCartBubble(cart);
      renderCartDrawer(cart, 'Your bag has been updated.');
      refreshPendingCartLines();
      state.resolve(cart);
    } catch (error) {
      cartLineUpdates.delete(key);
      const currentCart = await runCartMutation(() => getCart({ timeout: 4200 })).catch(() => null);
      if (currentCart) {
        syncCartBubble(currentCart);
        renderCartDrawer(currentCart, error?.message || 'Cart update failed.');
      } else {
        restoreCartLineAfterFailure(key, state.confirmedQuantity);
        if (cartDrawerSubtotal) cartDrawerSubtotal.textContent = 'View cart for total';
      }
      if (cartDrawerMessage) cartDrawerMessage.dataset.tone = 'error';
      refreshPendingCartLines();
      state.reject(error);
    }
  }
};

const startCartLineUpdateWorker = () => {
  if (cartUpdateWorker) return;
  cartUpdateWorker = flushCartLineUpdates().finally(() => {
    cartUpdateWorker = null;
    if (cartLineUpdates.size) startCartLineUpdateWorker();
  });
};

const updateCartLine = (key, quantity, confirmedQuantity = 0) => {
  if (!key) return Promise.resolve(null);
  const desiredQuantity = Math.max(0, Number(quantity) || 0);
  const existing = cartLineUpdates.get(key);
  if (existing) {
    existing.desiredQuantity = desiredQuantity;
    setCartLinePending(key, desiredQuantity);
    return existing.promise;
  }

  let resolveUpdate;
  let rejectUpdate;
  const promise = new Promise((resolve, reject) => {
    resolveUpdate = resolve;
    rejectUpdate = reject;
  });
  cartLineUpdates.set(key, {
    confirmedQuantity: Math.max(0, Number(confirmedQuantity) || 0),
    desiredQuantity,
    promise,
    resolve: resolveUpdate,
    reject: rejectUpdate
  });
  setCartLinePending(key, desiredQuantity);
  startCartLineUpdateWorker();
  return promise;
};
// CART_LINE_QUEUE_END

const updateCartBubbleFallback = (cart) => {
  const lineQuantityCount = Array.isArray(cart?.items)
    ? cart.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0)
    : 0;
  const itemCount = Math.max(Number(cart?.item_count ?? 0), lineQuantityCount);
  if (!Number.isFinite(itemCount)) return;
  document.querySelectorAll('[ref="cartBubbleCount"], .cart-bubble__text-count, [data-cart-count]').forEach((node) => {
    node.textContent = itemCount < 100 ? String(itemCount) : '';
    node.classList.toggle('hidden', itemCount === 0);
  });
  document.querySelectorAll('[ref="cartBubble"], .cart-bubble').forEach((node) => {
    node.classList.toggle('visually-hidden', itemCount === 0);
    node.classList.add('cart-bubble--animating');
    window.setTimeout(() => node.classList.remove('cart-bubble--animating'), 700);
  });
  document.querySelectorAll('cart-icon').forEach((node) => {
    node.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);
  });
  try {
    sessionStorage.setItem('cart-count', JSON.stringify({
      value: String(itemCount),
      timestamp: Date.now()
    }));
  } catch (error) {
    // Ignore storage failures in private browsing modes.
  }
};

const syncCartBubble = (cart) => {
  updateCartBubbleFallback(cart);
  document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: {
    resource: cart, sourceId: 'orlaven-cart',
    data: { source: 'orlaven-cart', itemCount: cart.item_count }
  }}));
};

const getCart = async ({ timeout = 12000 } = {}) => {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeout);
  const response = await fetch(`${routesRoot}cart.js`, {
    headers: { Accept: 'application/json' },
    signal: controller.signal
  }).finally(() => window.clearTimeout(timer));
  if (!response.ok) throw new Error('Unable to refresh your bag. Please try again.');
  return response.json();
};


const refreshAndOpen = async () => {
  renderCartDrawerPending();
  try {
    const cart = await runCartMutation(() => getCart());
    syncCartBubble(cart);
    renderCartDrawer(cart);
    refreshPendingCartLines();
  } catch (error) {
    cartDrawerMessage.textContent = error.message;
    cartDrawerMessage.dataset.tone = 'error';
    cartDrawerItems.innerHTML = `<a href="${escapeHtml(routesRoot + 'cart')}">View your bag</a>`;
  }
};
const add = async (formData, message = 'Added to your bag.') => {
  renderCartDrawerPending('Adding to your bag…');
  try {
    return await runCartMutation(async () => {
      const response = await fetch(`${routesRoot}cart/add.js`, {
        method: 'POST', headers: { Accept: 'application/json' }, body: formData
      });
      const result = await response.json();
      if (!response.ok || result.status >= 400) throw new Error(result.description || result.message || 'Unable to add this item.');
      // The add is already confirmed; a failed read must never trigger a duplicate add.
      try {
        const cart = await getCart();
        syncCartBubble(cart);
        renderCartDrawer(cart, message);
        refreshPendingCartLines();
        return { added: result, cart };
      } catch {
        cartDrawerMessage.textContent = 'Item added. Your bag could not refresh; open your bag to confirm it.';
        cartDrawerMessage.dataset.tone = 'error';
        cartDrawerItems.innerHTML = `<a href="${escapeHtml(routesRoot + 'cart')}">View your bag</a>`;
        return { added: result, cart: null };
      }
    });
  } catch (error) {
    cartDrawerMessage.textContent = error.message || 'Unable to add this item.';
    cartDrawerMessage.dataset.tone = 'error';
    cartDrawerItems.replaceChildren();
    throw error;
  }
};
window.OrlavenCart = { add, open: refreshAndOpen, close: closeCartDrawer, run: runCartMutation, message: showCartDrawerMessage };

cartDrawer.addEventListener('cancel', (event) => { event.preventDefault(); closeCartDrawer(); });
cartDrawer.addEventListener('click', async (event) => {
  const control = event.target.closest('[data-or-cart-close], [data-or-cart-quantity], [data-or-cart-checkout]');
  if (!control) return;
  if (control.matches('[aria-disabled="true"]')) { event.preventDefault(); return; }
  if (control.matches('[data-or-cart-close]')) { closeCartDrawer(); return; }
  if (!control.matches('[data-or-cart-quantity]')) return;
  const key = control.dataset.orCartKey;
  const row = control.closest('[data-or-cart-line-key]');
  const quantity = cartLineUpdates.get(key)?.desiredQuantity ?? Number(row?.querySelector('[data-or-cart-current-quantity]')?.textContent || 0);
  const next = control.matches('.orpdp__cart-remove') ? 0 : Math.max(0, quantity + Number(control.dataset.orCartQuantityDelta || 0));
  try { await updateCartLine(key, next, quantity); }
  catch (error) { cartDrawerMessage.textContent = error.message || 'Cart update failed.'; cartDrawerMessage.dataset.tone = 'error'; }
});
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-or-cart-open]');
  if (!trigger || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  refreshAndOpen();
});
// Native Horizon forms and the design builder keep their validations and publish this event.
document.addEventListener('cart:update', async (event) => {
  if (event.detail?.data?.source === 'orlaven-cart') return;
  const isAdd = event.detail?.data?.source === 'product-form-component';
  if (!isAdd && !cartDrawer.open) return;
  if (isAdd && event.detail?.data?.didError) return;
  if (isAdd) openCartDrawer();
  const cart = event.detail?.resource;
  if (Array.isArray(cart?.items)) {
    updateCartBubbleFallback(cart);
    renderCartDrawer(cart, isAdd ? 'Added to your bag.' : 'Your bag has been updated.');
    refreshPendingCartLines();
  } else await refreshAndOpen();
});
// Plain Shopify product forms (including footwear) need AJAX handling too.
document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || event.defaultPrevented) return;
  const component = form.closest('product-form-component');
  if (component && typeof component.handleSubmit === 'function') return;
  const action = new URL(form.action, location.href);
  if (action.origin !== location.origin || !/\/cart\/add(?:\.js)?$/.test(action.pathname)) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (form.dataset.cartAdding === 'true' || !form.reportValidity()) return;
  const payload = new FormData(form);
  const buttons = [...form.querySelectorAll('[type="submit"]')];
  const buttonStates = buttons.map(button => ({ button, label: button.textContent, disabled: button.disabled }));
  form.dataset.cartAdding = 'true';
  form.setAttribute('aria-busy', 'true');
  buttons.forEach(button => { button.disabled = true; button.textContent = 'Adding…'; });
  try { await add(payload); }
  catch (error) { console.error('Cart add failed:', error.message); }
  finally {
    delete form.dataset.cartAdding;
    form.removeAttribute('aria-busy');
    buttonStates.forEach(({button, label, disabled}) => { button.textContent = label; button.disabled = disabled; });
  }
}, true);
})();
